import { useCallback, useEffect, useState } from "react";
import { AlertCircle, CheckCircle2, ExternalLink, RefreshCw, Scale, ShieldAlert } from "lucide-react";
import { useT } from "../../../../i18n/I18nProvider";
import {
  acceptOpenDataLicence,
  getOpenDataLicence,
  withdrawOpenDataLicence,
  type LicenceSourceView,
} from "../../../../lib/openDataLicence";
import { Btn, StatusPill } from "../../../ui";
import { fmtDateTime } from "../../../ui/format";
import { ConnStatus, IntegrationCard, PanelLoader } from "../panel/IntegrationCard";
import { Note } from "../panel/fields";

/**
 * Settings -> Integrations: the terms the register's open data is kept under.
 *
 * Not a preference. RIK's terms say acceptance happens the moment the dataset
 * is opened, so by the time anybody reads this screen the agreement has already
 * been made a hundred times over by a nightly import. What the screen adds is
 * the RECORD - who agreed, to which issue, on what date - and the gate behind
 * it, which stops every download until that record exists.
 *
 * Four sources are listed and two of them have a button. RIK and the public
 * procurement register publish their terms; EMTA's and MTR's have not been
 * established, and putting their imports behind a record nobody can write would
 * be inventing an obligation rather than recording one. The screen says that in
 * words instead of hiding it.
 */
export function OpenDataLicenceCard() {
  const t = useT();
  const [sources, setSources] = useState<LicenceSourceView[] | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState(false);

  const load = useCallback(() => {
    getOpenDataLicence()
      .then((r) => setSources(r.sources))
      .catch(() => setSources([]));
  }, []);

  useEffect(() => load(), [load]);

  async function act(source: LicenceSourceView, next: "accept" | "withdraw") {
    if (next === "accept" && !source.terms) return;
    setBusy(source.source);
    setError(false);
    try {
      if (next === "accept" && source.terms) {
        // The issue that was ON SCREEN goes back, not "whatever is current".
        // The server refuses a mismatch, which is what a tab left open across a
        // deploy that changed the terms deserves.
        await acceptOpenDataLicence({
          source: source.source,
          termsId: source.terms.id,
          version: source.terms.version,
        });
      } else {
        await withdrawOpenDataLicence(source.source);
      }
      load();
    } catch {
      setError(true);
    } finally {
      setBusy(null);
    }
  }

  if (!sources) return <PanelLoader />;

  // The head speaks for the whole card, so it reports the state that stops
  // work: one blocked source is a blocked mirror.
  const allOk = sources.every((s) => s.ok);

  return (
    <IntegrationCard
      icon={Scale}
      title={t("settings.openData.title")}
      desc={t("settings.openData.desc")}
      status={
        <ConnStatus
          ok={allOk}
          okLabel={t("settings.openData.statusAccepted")}
          offLabel={t("settings.openData.statusBlocked")}
        />
      }
    >
      <div className="space-y-6">
        <Note tone="info" icon={<ShieldAlert className="mt-0.5 size-4 shrink-0" />}>
          {t("settings.openData.intro")}
        </Note>

        {sources.map((source) => (
          <SourceBlock
            key={source.source}
            source={source}
            busy={busy === source.source}
            onAct={(next) => act(source, next)}
          />
        ))}

        {error ? <StatusPill tone="lt">{t("settings.openData.saveError")}</StatusPill> : null}
      </div>
    </IntegrationCard>
  );
}

/** One publisher: what it publishes under, who agreed, and the one button. */
function SourceBlock({
  source,
  busy,
  onAct,
}: {
  source: LicenceSourceView;
  busy: boolean;
  onAct: (next: "accept" | "withdraw") => void;
}) {
  const t = useT();
  const { terms, accepted } = source;

  return (
    <section className="rounded-[20px] border border-[var(--crm-hair)] p-5">
      <header className="flex flex-wrap items-center justify-between gap-3">
        <div className="min-w-0">
          <h3 className="crm-trunc text-[14px] font-[650]">{t(`settings.openData.source.${source.source}`)}</h3>
          <p className="crm-sub !mt-1 font-mono text-[11.5px]">{source.host}</p>
        </div>
        {terms ? (
          <StatusPill tone={source.ok ? "ok" : "lt"}>
            {source.ok ? t("settings.openData.accepted") : t("settings.openData.notAccepted")}
          </StatusPill>
        ) : (
          <StatusPill tone="mut">{t("settings.openData.noTerms")}</StatusPill>
        )}
      </header>

      {terms ? (
        <div className="mt-4 space-y-4">
          <dl className="grid gap-x-6 gap-y-2 text-[12.75px] sm:grid-cols-2">
            <Row label={t("settings.openData.licence")}>
              <Link href={terms.licenceUrl}>{terms.licenceName}</Link>
            </Row>
            <Row label={t("settings.openData.issue")}>
              <span className="font-mono text-[11.5px]">{terms.version}</span>
            </Row>
            <Row label={t("settings.openData.terms")}>
              <Link href={terms.termsUrl}>{t("settings.openData.openTerms")}</Link>
            </Row>
            <Row label={t("settings.openData.files")}>
              <Link href={terms.downloadUrl}>{t("settings.openData.openFiles")}</Link>
            </Row>
          </dl>

          {/* The two things the licence does NOT do. Both are load bearing and
              neither is obvious from its name, which is why they are a block of
              prose rather than two ticks in the table above. Both sentences
              are about RIK's licence and RIK's own pages, so they are drawn for
              RIK only; the procurement register gets its own. */}
          {source.source === "rik" ? (
            <Note tone="warn" icon={<AlertCircle className="mt-0.5 size-4 shrink-0" />}>
              <span className="block">{t("settings.openData.noDataProtection")}</span>
              <span className="mt-1.5 block">{t("settings.openData.contradiction")}</span>
            </Note>
          ) : null}
          {source.source === "rhr" ? (
            <Note tone="info" icon={<AlertCircle className="mt-0.5 size-4 shrink-0" />}>
              {t("settings.openData.rhrNote")}
            </Note>
          ) : null}

          <ul className="crm-sub space-y-1.5 pl-4 text-[12.25px] leading-relaxed [&>li]:list-disc">
            {terms.attributionRequired ? <li>{t("settings.openData.dutyAttribution")}</li> : null}
            {terms.shareAlike ? <li>{t("settings.openData.dutyShareAlike")}</li> : null}
            <li>{t("settings.openData.dutyGate")}</li>
          </ul>

          {accepted ? (
            <Note tone={source.ok ? "ok" : "bad"} icon={<CheckCircle2 className="mt-0.5 size-4 shrink-0" />} live>
              <span className="block">
                {t("settings.openData.acceptedBy", {
                  who: accepted.acceptedBy?.name || accepted.acceptedBy?.email || t("settings.openData.unknownWho"),
                  when: fmtDateTime(accepted.acceptedAt),
                  version: accepted.version,
                })}
              </span>
              {/* A stale record is not a missing one, and the sentence has to
                  say which. "Nobody accepted this" would send somebody looking
                  for the wrong thing. */}
              {source.block === "stale" ? (
                <span className="mt-1.5 block font-[650]">{t("settings.openData.stale")}</span>
              ) : null}
              {source.block === "other_terms" ? (
                <span className="mt-1.5 block font-[650]">{t("settings.openData.otherTerms")}</span>
              ) : null}
            </Note>
          ) : (
            <Note tone="bad" icon={<AlertCircle className="mt-0.5 size-4 shrink-0" />}>
              {t("settings.openData.blocked")}
            </Note>
          )}

          <div className="flex flex-wrap items-center gap-3">
            <Btn tone={source.ok ? "plain" : "dark"} disabled={busy} onClick={() => onAct("accept")}>
              {busy ? <RefreshCw className="size-3.5 animate-spin" /> : <Scale className="size-3.5" />}
              {source.ok ? t("settings.openData.reaccept") : t("settings.openData.accept")}
            </Btn>
            {accepted ? (
              <Btn tone="danger" disabled={busy} onClick={() => onAct("withdraw")}>
                {t("settings.openData.withdraw")}
              </Btn>
            ) : null}
          </div>
        </div>
      ) : (
        <div className="mt-4 space-y-3">
          <Note tone="mut">{t("settings.openData.termsUnknown")}</Note>
          <Link href={source.infoUrl}>{t("settings.openData.openSource")}</Link>
        </div>
      )}
    </section>
  );
}

function Row({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex min-w-0 items-baseline gap-2">
      <dt className="crm-sub shrink-0 text-[11.5px]">{label}</dt>
      <dd className="min-w-0 flex-1 truncate">{children}</dd>
    </div>
  );
}

/** An outward link. `noreferrer` because these leave the CRM for a public site. */
function Link({ href, children }: { href: string; children: React.ReactNode }) {
  return (
    <a
      href={href}
      target="_blank"
      rel="noreferrer noopener"
      className="inline-flex items-center gap-1 underline decoration-dotted underline-offset-2"
    >
      <span className="truncate">{children}</span>
      <ExternalLink className="size-3 shrink-0 opacity-70" />
    </a>
  );
}
